import {
  TextInput as AdminTextInput,
  ArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DeleteWithConfirmButton,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  List as RAList,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  useGetIdentity,
  useRecordContext,
  useRefresh,
} from "react-admin";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  TextField as MuiTextField,
} from "@mui/material";
import { get, set } from "lodash";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CompanyIngresType } from "src/@generated/schemas";
import { GlobalListStyles } from "src/libs/globalListStyles";
import i18next from "src/locales";
import { url } from "src";
import { useFormContext } from "react-hook-form";
import { useRef } from "react";
import { useUploadCompanyLogoMutation } from "../../@generated/hooks";

const DefaultValueType = {
  DEFAULT_LOADING_LOCATION_ID: "Defaultní místo nakládky (ID)",
  DEFAULT_LOADING_CITY: "Defaultní město nakládky (Název)",
  DEFAULT_UNLOADING_LOCATION_ID: "Defaultní místo vykládky (ID)",
  DEFAULT_UNLOADING_CITY: "Defaultní město vykládky (Název)",
  LOADING_DATE: "Automatické přidání/odebrání dnu do datumu nakládky",
  UNLOADING_DATE: "Automatické přidání/odebrání dnu do datumu vykládky",
  ORDER_TYPE: "Typ zboží",
  CURRENCY: "Měna",
  PROFIT_CENTER: "Defaultní středisko",
  // ENABLED_DELIVERY_NOTE_HALF_PALLETS: "Povolen počet půl palet",
  // ENABLED_DELIVERY_NOTE_PACKAGES: "Povoleno počet balíků",
  // ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES:
  //   "Povoleno počet nadrozměrných palet",
  ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION:
    "Nastavit povinné zadávání vratných obalů",
  ENABLED_MULTIPLE_PROFIT_CENTERS: "Povoleno více středisek",
  ENABLED_MULTIPLE_ACTIONS: "Povoleno více akcí",
  ACTION: "Defaultní akce",
  AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE:
    "Kopírovat referenční číslo do názvu dodacího listu (prvního vytvořeného)",
  AUTO_COPY_PALLET_PLACES_TO_SSCC:
    "Počet paletových míst kopírovat do počtu štítků (prvního vytvořeného)",
  AUTO_COPY_REFERENCE_NUMBER_TO_CODE:
    "Kopírovat referenční číslo do kódu nakládky",
  SPECIFIC_LOADING_UNITS: "Nastavení vratných obalů (multi)",
  DEFAULT_LOADING_UNIT: "Nastavení defaultní vratný obal",
  AUTO_COPY_SSCC_TO_LOADING_UNITS:
    "Kopírovat počet štítků do počtu vratných obalů",
  ENABLED_PREFIX_REFERENCE_NUMBER:
    "Povolit prefix pro referenční číslo (Datum ve formátu YYYY_DD_MM_HH_MM)",
  SPECIFIC_MULTIPLE_PROFIT_CENTERS:
    "Definice povolených středisek (multi)",
  ENABLED_SALES_TYPES:
    "Povolit možnost výberu ceníku",
};

export const convertStringToNumber = (value) => {
  const float = parseFloat(value);
  return isNaN(float) ? null : value;
};

const transform = (data) => {
  return data;
};

export const Form = (props) => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { setValue } = useFormContext();
  const [upload, { loading }] = useUploadCompanyLogoMutation();
  const uploadInputRef = useRef<any>();

  const onLogoChange = async (event) => {
    const files = event.target.files;

    try {
      await upload({
        variables: {
          file: files[0],
          companyId: record.id as string,
        },
      });

      refresh();

      uploadInputRef.current.value = null;
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <TextInput source="name" style={{width: 400}} />
      <br />
      <TextInput source="carloExternalId" style={{width: 400}} />
      <br />
      <TextInput source="carloPartnerId" style={{width: 400}} />
      <br />
      <TextInput source="ingresId" style={{width: 400}} />
      <br />
      <BooleanInput source="isBlocked" />
      <br />
      <SelectInput
        source="ingresType"
        style={{width: 400}}
        choices={[
          {
            id: CompanyIngresType.Cz,
            name: "Česká",
          },
          {
            id: CompanyIngresType.Sk,
            name: "Slovenská",
          },
        ]}
      />
      <br />

      <label htmlFor="upload-file">
        <input
          ref={uploadInputRef}
          id="upload-file"
          onChange={onLogoChange}
          type="file"
          hidden
        />
        <Button
          style={{
            marginBottom: 20,
          }}
          disabled={loading}
          variant="contained"
          onClick={() => uploadInputRef?.current?.click()}
        >
          {i18next.translate('resources.Company.fields.uploadLogo')}
          {loading ? (
            <CircularProgress
              size="1rem"
              color="inherit"
              style={{ marginLeft: 8 }}
            />
          ) : (
            <CloudUploadIcon style={{ marginLeft: 8 }} />
          )}
        </Button>

        {record?.logo ? (
          <img
            style={{ marginLeft: 12 }}
            height={50}
            src={`${url}/uploaded_logo/${record.logo}`}
          />
        ) : null}
      </label>

      {record?.id && (
        <ArrayInput source="companyDefaultValues" style={{width: '100%'}} title={i18next.translate('resources.Company.fields.defaultValues')}>
          <SimpleFormIterator inline>
            <SelectInput
              source="property"
              label="Nastavení"
              style={{width: 400}}
              choices={Object.keys(DefaultValueType).map((id) => ({
                id,
                name: DefaultValueType[id],
              }))}
              required
            />
            <FormDataConsumer style={{ width: 500 }}>
              {({ scopedFormData, formData, getSource, ...rest }) => {
                const property = scopedFormData?.property;
                if (!property) return null;

                if (
                  [
                    "PROFIT_CENTER",
                    "DEFAULT_LOADING_LOCATION_ID",
                    "DEFAULT_LOADING_CITY",
                    "DEFAULT_LOADING_LOCATION_ID",
                    "DEFAULT_UNLOADING_LOCATION_ID",
                    "DEFAULT_UNLOADING_CITY",
                    "ORDER_TYPE",
                    "DEFAULT_LOADING_UNIT",
                    "ACTION",
                  ].includes(property)
                ) {
                  return (
                    <TextInput label="Hodnota" source={getSource("value")} required {...rest} />
                  );
                } else if (
                  ["LOADING_DATE", "UNLOADING_DATE"].includes(
                    property
                  )
                ) {
                  return (
                    <TextInput
                      defaultValue={0}
                      label="Hodnota"
                      parse={convertStringToNumber}
                      source={getSource("value")}
                      required
                      {...rest}
                    />
                  );
                } else if (["CURRENCY"].includes(property)) {
                  return (
                    <SelectInput
                      source={getSource("value")}
                      size="small"
                      label="Měna"
                      choices={[
                        {
                          id: "CZK",
                          name: "CZK",
                        },
                        {
                          id: "EUR",
                          name: "EUR",
                        },
                      ]}
                    />
                  );
                } else if (
                  [
                    "ENABLED_DELIVERY_NOTE_HALF_PALLETS",
                    "ENABLED_DELIVERY_NOTE_PACKAGES",
                    "ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES",
                    "ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION",
                    "ENABLED_MULTIPLE_PROFIT_CENTERS",
                    "ENABLED_MULTIPLE_ACTIONS",
                    "ENABLED_PREFIX_REFERENCE_NUMBER",
                    "AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE",
                    "AUTO_COPY_PALLET_PLACES_TO_SSCC",
                    "AUTO_COPY_REFERENCE_NUMBER_TO_CODE",
                    "AUTO_COPY_SSCC_TO_LOADING_UNITS",
                    "ENABLED_SALES_TYPES",
                  ].includes(property)
                ) {
                  return (
                    <SelectInput
                      source={getSource("value")}
                      size="small"
                      label="Hodnota"
                      choices={[
                        {
                          id: "TRUE",
                          name: "Ano",
                        },
                        {
                          id: "FALSE",
                          name: "Ne",
                        },
                      ]}
                    />
                  );
                } else if (
                  ["SPECIFIC_LOADING_UNITS", "SPECIFIC_MULTIPLE_PROFIT_CENTERS"].includes(property)
                ) {
                  return (
                    <Autocomplete
                      multiple
                      freeSolo
                      style={{ width: 400 }}
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              variant="outlined"
                              label={option}
                              key={key}
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          size="small"
                          variant="filled"
                          label="Values"
                          placeholder=""
                        />
                      )}
                      onChange={(_, values) =>
                        setValue(
                          "companyDefaultValues",
                          set(
                            formData,
                            getSource("value"),
                            JSON.stringify(values)
                          )[
                          "companyDefaultValues"
                          ]
                        )
                      }
                      options={[]}
                      defaultValue={JSON.parse(
                        get(formData, getSource("value")) || "[]"
                      )}
                    />
                  );
                }

                return null;
              }}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      )}
    </div>
  );
};

export const CompanyCreate = () => (
  <Create>
    <SimpleForm
      defaultValues={{
        ingresType: CompanyIngresType.Cz,
      }}
    >
      <Form />
    </SimpleForm>
  </Create>
);

export const CompanyEdit = () => {
  const { refetch } = useGetIdentity();

  const handleOnSave = () => {
    refetch();
  };

  return (
    <Edit
      mutationMode="pessimistic"
      transform={transform}
      queryOptions={{ onSuccess: () => handleOnSave() }}
    >
      <SimpleForm key="form" shouldUnregister={false}>
        <Form />
      </SimpleForm>
    </Edit>
  );
};

const CompanyFilter = (props: any) => (
  <Filter {...props}>
    <AdminTextInput
      source="name"
      label="Vyhledat firmu"
      alwaysOn
      clearAlwaysVisible
    />
  </Filter>
);

export const CompaniesList = (props: any) => {
  return (
    <RAList
      {...props}
      filters={<CompanyFilter />}
      exporter={false}
      filter={{
        deletedAt: null,
      }}
      sx={{
        width: "calc(100vw - 50px)",
      }}
    >
      <GlobalListStyles />
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 180px)",
          overflow: "hidden",
          width: "calc(100vw - 50px)",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Datagrid>
            <TextField source="name" />
            <TextField source="ingresType" />
            <TextField source="carloExternalId" />
            <TextField source="carloPartnerId" />
            <BooleanField source="isBlocked" label="Zablokován" />
            <EditButton />
            <DeleteWithConfirmButton />
          </Datagrid>
        </Box>
      </Card>
    </RAList>
  );
};
